import { FC, ReactElement, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { ResumoPolitica } from '@/components/produtos/PoliticaVigentePainel'
import { ehConsultor } from '@/components/layout/menu'
import { authStore } from '@/store/authStore'
import {
  mensagemErroPolitica,
  politicaProdutoService,
  rotaDeAcaoValida,
  statusErroPolitica,
} from '@/services/politicaProdutoService'
import {
  PoliticaPendente,
  ROTULO_COMPONENTE,
  ROTULO_OWNER,
  rotulo,
} from '@/types/politicaProduto'
import { formatarDataIso } from '@/components/elegibilidade/formatoElegibilidade'
import { formatDateTime } from '@/utils/formatters'

const TEXTO_PROPRIO_PROPONENTE = 'Você propôs esta versão: a aprovação é de outra pessoa.'

/** Por que o usuário logado não age sobre o item (o backend é a trava; a tela só explica). */
/**
 * Por que o usuário logado não age sobre o item (o backend é a trava; a tela só explica). Rejeitar também fica
 * desabilitado ao proponente: a simulação recusa (`validarAprovacao` vale para as duas decisões) e, para manter uma
 * regra única na fila, a tela não oferece a rejeição da própria proposta em nenhum módulo. Papel sem alçada
 * (403 `APROVADOR_SEM_PAPEL`) não é antecipado: o erro do backend aparece como veio.
 */
const bloqueioDaAcao = (p: PoliticaPendente, eu: string | null | undefined, rota: string | null | undefined): string | null => {
  if (eu && p.propostoPor && p.propostoPor === eu) return TEXTO_PROPRIO_PROPONENTE
  if (!rotaDeAcaoValida(rota)) return 'Ação indisponível: o servidor não informou a rota no módulo dono.'
  return null
}

const vigenciaPretendida = (p: PoliticaPendente): string =>
  p.vigenciaInicio ? `a partir de ${formatarDataIso(p.vigenciaInicio)}` : '—'

/**
 * P6 (AN-PP6 §2.4/§8) — fila de aprovação das políticas do produto (back-office).
 *
 * Lista `GET /v1/produtos/politicas/pendentes`: componente, produto, proponente, vigência pretendida e o resumo da
 * proposta (o backend não manda a ativa no item: a comparação fica na aba "Política vigente" do produto).
 * Aprovar/Rejeitar chamam `linkAprovar`/`linkRejeitar` (módulo dono) que vieram no item; ficam
 * desabilitados para o próprio proponente (o backend recusa com 403 `APROVACAO_PELO_PROPONENTE` de qualquer forma).
 * Política é global (P-11): a fila é a mesma para qualquer tenant.
 */
const PoliticasPendentesPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const consultor = ehConsultor(usuario?.permissoes)
  const eu = usuario?.id ?? null

  const [itens, setItens] = useState<PoliticaPendente[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [semPermissao, setSemPermissao] = useState(false)
  const [erroAcao, setErroAcao] = useState<string | null>(null)
  const [sucesso, setSucesso] = useState<string | null>(null)
  const [enviando, setEnviando] = useState<string | null>(null)
  const [rejeitando, setRejeitando] = useState<PoliticaPendente | null>(null)
  const [motivo, setMotivo] = useState('')

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    setSemPermissao(false)
    try {
      setItens(await politicaProdutoService.pendentes())
    } catch (e) {
      setItens([])
      if (statusErroPolitica(e) === 403) setSemPermissao(true)
      setErro(mensagemErroPolitica(e, 'Não foi possível carregar a fila de aprovação.'))
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    if (!consultor) void carregar()
  }, [carregar, consultor])

  const chave = (p: PoliticaPendente) => `${p.componente}:${p.politicaId}`
  const nomeItem = (p: PoliticaPendente) =>
    `${rotulo(ROTULO_COMPONENTE, p.componente)}${p.versao != null ? ` versão ${p.versao}` : ''}`

  const aprovar = async (p: PoliticaPendente) => {
    if (!p.linkAprovar) return
    setEnviando(chave(p))
    setErroAcao(null)
    setSucesso(null)
    try {
      // Revisão PP6-valr (A1): o link já identifica a proposta (id); o hash prova que se aprova o conteúdo revisado.
      await politicaProdutoService.aprovar(p.linkAprovar, p.hashConteudo ? { conteudoHash: p.hashConteudo } : {})
      setSucesso(`${nomeItem(p)} aprovada.`)
      await carregar()
    } catch (e) {
      setErroAcao(mensagemErroPolitica(e, 'Não foi possível aprovar.'))
    } finally {
      setEnviando(null)
    }
  }

  /** Revisão PP6-valr (8b): o PRÓPRIO proponente retira a proposta (sem segunda pessoa) e libera a chave. */
  const retirar = async (p: PoliticaPendente) => {
    if (!p.linkRetirar) return
    setEnviando(chave(p))
    setErroAcao(null)
    setSucesso(null)
    try {
      await politicaProdutoService.retirar(p.linkRetirar)
      setSucesso(`${nomeItem(p)} retirada.`)
      await carregar()
    } catch (e) {
      setErroAcao(mensagemErroPolitica(e, 'Não foi possível retirar a proposta.'))
    } finally {
      setEnviando(null)
    }
  }

  const confirmarRejeicao = async () => {
    const p = rejeitando
    if (!p?.linkRejeitar || !motivo.trim()) return
    setEnviando(chave(p))
    setErroAcao(null)
    setSucesso(null)
    try {
      await politicaProdutoService.rejeitar(p.linkRejeitar, { motivo: motivo.trim() })
      setSucesso(`${nomeItem(p)} rejeitada.`)
      setRejeitando(null)
      setMotivo('')
      await carregar()
    } catch (e) {
      setErroAcao(mensagemErroPolitica(e, 'Não foi possível rejeitar.'))
    } finally {
      setEnviando(null)
    }
  }

  const cabecalho = (
    <PageHeader
      title="Políticas pendentes de aprovação"
      eyebrow="Catálogo de Produtos"
      subtitle="Versões propostas aguardando a aprovação de outra pessoa. Até lá, nada muda para quem simula ou reserva."
    />
  )

  // A rota já devolve o consultor ao dashboard (`SomenteBackoffice`); aqui só por garantia — sem chamada nem dado.
  if (consultor) {
    return (
      <Box>
        {cabecalho}
        <EstadoDeLista estado="semPermissao" />
      </Box>
    )
  }

  let corpo: ReactElement
  if (carregando) {
    corpo = <EstadoDeLista estado="carregando" mensagem="Carregando a fila de aprovação..." />
  } else if (semPermissao) {
    corpo = <EstadoDeLista estado="semPermissao" detalhe={erro ?? undefined} />
  } else if (erro) {
    corpo = <EstadoDeLista estado="erro" detalhe={erro} acao={{ label: 'Tentar novamente', onClick: () => void carregar() }} />
  } else if (itens.length === 0) {
    corpo = <EstadoDeLista estado="vazio" mensagem="Nenhuma política aguardando aprovação" />
  } else {
    corpo = (
      <Stack spacing={2}>
        {itens.map((p) => {
          const bloqueioAprovar = bloqueioDaAcao(p, eu, p.linkAprovar)
          const bloqueioRejeitar = bloqueioDaAcao(p, eu, p.linkRejeitar)
          const ocupado = enviando === chave(p)
          const souProponente = !!eu && !!p.propostoPor && p.propostoPor === eu
          const proponente = souProponente ? 'você' : p.propostoPor
          return (
            <Paper
              key={chave(p)}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2 }}
              component="section"
              aria-label={`Pendente: ${nomeItem(p)}`}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {rotulo(ROTULO_COMPONENTE, p.componente)}
                    {p.discriminador ? ` — ${p.discriminador}` : ''}
                    {p.versao != null ? ` · versão ${p.versao}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Produto: {p.produtoCodigo ?? 'global (todos os produtos)'}
                  </Typography>
                </Box>
                <Chip size="small" color="warning" label="Pendente de aprovação" sx={{ alignSelf: 'flex-start' }} />
              </Stack>

              <Stack spacing={0.25} sx={{ mb: 1.5 }}>
                <Typography variant="body2">
                  Proposta por {proponente ?? '—'}
                  {p.propostoEm ? ` em ${formatDateTime(p.propostoEm)}` : ''}
                </Typography>
                <Typography variant="body2">Vigência pretendida: {vigenciaPretendida(p)}</Typography>
                {p.owner && <Typography variant="body2">Área dona: {rotulo(ROTULO_OWNER, p.owner)}</Typography>}
                {p.origem && <Typography variant="body2" color="text.secondary">Origem: {p.origem}</Typography>}
                {p.moduloOrigem && (
                  <Typography variant="body2" color="text.secondary">Módulo dono: {p.moduloOrigem}</Typography>
                )}
              </Stack>

              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                  Proposta
                </Typography>
                <ResumoPolitica valor={p.resumo} />
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <Button
                  variant="contained"
                  disabled={!!bloqueioAprovar || ocupado}
                  onClick={() => void aprovar(p)}
                >
                  Aprovar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={!!bloqueioRejeitar || ocupado}
                  onClick={() => {
                    setMotivo('')
                    setErroAcao(null)
                    setRejeitando(p)
                  }}
                >
                  Rejeitar
                </Button>
                {souProponente && rotaDeAcaoValida(p.linkRetirar) && (
                  <Button variant="text" color="warning" disabled={ocupado} onClick={() => void retirar(p)}>
                    Retirar proposta
                  </Button>
                )}
                {(bloqueioAprovar ?? bloqueioRejeitar) && (
                  <Typography variant="body2" color="text.secondary">
                    {bloqueioAprovar ?? bloqueioRejeitar}
                  </Typography>
                )}
              </Stack>
            </Paper>
          )
        })}
      </Stack>
    )
  }

  return (
    <Box>
      {cabecalho}
      {sucesso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso(null)}>
          {sucesso}
        </Alert>
      )}
      {erroAcao && !rejeitando && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroAcao(null)} data-testid="erro-acao">
          {erroAcao}
        </Alert>
      )}
      {corpo}

      <Dialog open={!!rejeitando} onClose={() => (enviando ? undefined : setRejeitando(null))} fullWidth maxWidth="sm">
        <DialogTitle>Rejeitar {rejeitando ? nomeItem(rejeitando) : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A versão fica no histórico como rejeitada. O motivo é obrigatório e fica registrado.
          </Typography>
          {erroAcao && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="erro-rejeicao">
              {erroAcao}
            </Alert>
          )}
          <TextField
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejeitando(null)} disabled={!!enviando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!motivo.trim() || !!enviando}
            onClick={() => void confirmarRejeicao()}
          >
            Confirmar rejeição
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PoliticasPendentesPage
